import { createRouter, isNavigationFailure, parseQuery } from 'vue-router'
import { ElMessage } from 'element-plus'
import * as authApi from '@/http/modules/auth'
import store from '@/store'
import { clearSessionProfile, saveSessionProfile } from '@/utils/authSession'
import { ensureAccessInitialized, resetAccess } from '@/router/accessRuntime'
import { clearActiveResourceId, setActiveResourceId } from '@/utils/resourceContext.mjs'
import { createRootUrlHistory } from '@/router/rootUrlHistory.mjs'
import {
  consumePendingRootTarget, getRootNavigationIdentity, getRootNavigationScope,
  prepareRootNavigationReload, ROOT_NAVIGATION_HISTORY_KEY, ROOT_PATH,
} from '@/utils/rootNavigationState.mjs'

const PASSWORD_CHANGE_REQUIRED_PATH = '/password-change-required'

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/Login.vue'),
    meta: { public: true },
  },
  {
    path: '/resources',
    name: 'resources',
    component: () => import('../views/Sys/Resource.vue'),
    meta: { public: true },
  },
  {
    path: PASSWORD_CHANGE_REQUIRED_PATH,
    name: 'passwordChangeRequired',
    component: () => import('../views/PasswordChangeRequired.vue'),
    meta: { passwordChangeRequired: true },
  },
  {
    path: '/',
    name: 'home',
    component: () => import('../views/Home.vue'),
    children: [
      { path: '/intro', name: 'intro', component: () => import('../views/Intro.vue') },
    ],
  },
]

const navigationHistory = createRootUrlHistory()
const router = createRouter({
  history: navigationHistory,
  parseQuery: navigationHistory.createQueryParser(parseQuery),
  routes,
})
let cachedSession = null
let sessionPromise = null
let sessionGeneration = 0
let notifiedAccessError = ''

async function loadSession() {
  if (cachedSession) return cachedSession
  if (sessionPromise) return sessionPromise
  const generation = sessionGeneration
  let currentPromise
  currentPromise = (async () => {
    try {
      const response = await authApi.session()
      if (generation !== sessionGeneration) return null
      if (response?.code !== 200) return null
      await saveSessionProfile(response.data)
      if (generation !== sessionGeneration) return null
      cachedSession = response.data
      return cachedSession
    } catch (error) {
      return null
    } finally {
      if (sessionPromise === currentPromise) sessionPromise = null
    }
  })()
  sessionPromise = currentPromise
  return sessionPromise
}

export function cacheAuthenticatedSession(profile) {
  sessionGeneration += 1
  sessionPromise = null
  cachedSession = profile || null
}

export function clearAuthenticatedSession() {
  sessionGeneration += 1
  cachedSession = null
  sessionPromise = null
}

/** 登录完成后消费一次明确入口；权限路由由调用方预先初始化。 */
export function resolveAuthenticatedStart(profile) {
  const target = consumePendingRootTarget(profile)
  return target && router.resolve(target).matched.length ? target : '/intro'
}

function replaceRootRoute(target) {
  const { path, query, hash } = router.resolve(target)
  // 重定向到已显示的页面时也替换物理记录，避免后退旧记录留下错误入口。
  return { path, query, hash, replace: true, force: true }
}

if (typeof window !== 'undefined') {
  window.addEventListener('auth-session-cleared', clearAuthenticatedSession)
  window.addEventListener('password-change-required', () => {
    clearAuthenticatedSession()
    resetAccess()
    if (router.currentRoute.value.path !== PASSWORD_CHANGE_REQUIRED_PATH) {
      const reloadRequiredPage = () => {
        prepareRootNavigationReload(PASSWORD_CHANGE_REQUIRED_PATH)
        window.location.replace(ROOT_PATH)
      }
      router.replace(PASSWORD_CHANGE_REQUIRED_PATH).then((failure) => {
        if (isNavigationFailure(failure)) reloadRequiredPage()
      }).catch(reloadRequiredPage)
    }
  })
}

router.beforeEach(async (to) => {
  const generation = sessionGeneration
  const session = await loadSession()
  if (generation !== sessionGeneration) return false
  if (!session) {
    if (to.meta.public) return true
    clearAuthenticatedSession()
    resetAccess()
    clearSessionProfile({ preserveNavigationTarget: true })
    return replaceRootRoute('/login')
  }

  if (Boolean(session.mustChangePassword || session.recoverySession)) {
    resetAccess()
    return to.path === PASSWORD_CHANGE_REQUIRED_PATH ? true : replaceRootRoute(PASSWORD_CHANGE_REQUIRED_PATH)
  }

  if (to.path === PASSWORD_CHANGE_REQUIRED_PATH) return replaceRootRoute('/intro')

  // 浏览器旧记录必须属于当前身份和导航代次，不能把前一个账号的入口带回来。
  const historyContext = navigationHistory.state?.[ROOT_NAVIGATION_HISTORY_KEY]
  if (to.fullPath === navigationHistory.location && historyContext
    && (historyContext.scope !== getRootNavigationScope()
      || (historyContext.identity && historyContext.identity !== getRootNavigationIdentity()))
    && to.path !== '/intro' && !to.meta.public) return replaceRootRoute('/intro')

  try {
    await ensureAccessInitialized(router, { userId: session.userId })
    notifiedAccessError = ''
  } catch (error) {
    const message = store.state.access.error || '菜单权限加载失败'
    if (message !== notifiedAccessError) {
      notifiedAccessError = message
      ElMessage.error(message)
    }
    return to.path === '/intro' ? true : replaceRootRoute('/intro')
  }

  if (to.meta.public) return replaceRootRoute(resolveAuthenticatedStart(session))

  if (!to.matched.length) {
    const resolved = router.resolve(to.fullPath)
    return replaceRootRoute(resolved.matched.length ? to.fullPath : '/intro')
  }
  return true
})

router.afterEach((to, from, failure) => {
  if (failure) {
    navigationHistory.reportNavigationFailure(to)
    return
  }
  navigationHistory.confirmCurrentEntry()
  // 已登录入口成功落地后也消费启动目标，避免之后登录页误恢复旧入口。
  if (cachedSession && !to.meta.public && to.path !== PASSWORD_CHANGE_REQUIRED_PATH) {
    consumePendingRootTarget(cachedSession)
  }
  const resourceId = to.meta?.resourceId || ''
  if (resourceId) setActiveResourceId(resourceId)
  else clearActiveResourceId()
})

router.onError((error, to) => {
  navigationHistory.reportNavigationFailure(to)
  console.error('[Router Navigation]', error)
})

export default router
